import { Info } from "@phosphor-icons/react";
import clsx from "clsx";
import { type DragEvent, useRef } from "react";
import { toast } from "react-toastify";

import Loader from "common/components/Loader";

import useParseCsv from "../../../../datahooks/useParseCsv";
import UploadIcon from "./UploadIcon";

import type { CsvState } from "../../../../types";

const MAX_FILE_BYTES = 50 * 1024 * 1024;

function decodeUtf8OrWin1252(buf: ArrayBuffer | ArrayBufferView): string {
  const bytes =
    buf instanceof Uint8Array ? buf : new Uint8Array(buf as ArrayBuffer);

  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    try {
      return new TextDecoder("windows-1252").decode(bytes);
    } catch {
      return new TextDecoder("utf-8").decode(bytes);
    }
  }
}

export default function CsvUpload({
  onSuccess,
}: {
  onSuccess: (data: CsvState) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { parseCsv, isParsingCsv } = useParseCsv();

  function handleClick() {
    if (!isParsingCsv) fileInputRef.current?.click();
  }

  async function parseFile(file: File) {
    if (!file) {
      toast.error("No file selected");
      return;
    }

    if (file.type !== "text/csv") {
      toast.error(
        "Upload failed. Please make sure you are uploading a CSV file.",
      );
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      toast.error("File exceeds maximum size of 50MB");
      return;
    }

    try {
      const buf = await file.arrayBuffer();
      const text = decodeUtf8OrWin1252(buf);
      const data = await parseCsv({ csv: text });

      onSuccess({
        fileName: file.name,
        mainIdentifier: data.url_columns.length ? data.url_columns[0] : null,
        ...data,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch {
      toast.error(
        "Error reading or parsing the file. Please check the CSV and try again.",
      );
    }
  }

  function onDrop(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (isParsingCsv) return;

    parseFile(e.dataTransfer.files[0]);

    e.currentTarget.setAttribute("data-dragging", "false");
  }

  function onDragOver(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  function onDragEnter(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.currentTarget.setAttribute("data-dragging", "true");
  }

  function onDragLeave(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.currentTarget.setAttribute("data-dragging", "false");
  }

  return (
    <section className="mx-auto flex w-full max-w-[790px] flex-col gap-y-4 lg:mt-4">
      <button
        type="button"
        className={clsx(
          "group flex min-h-[211px] flex-col items-center gap-y-3 rounded-3xl border border-dashed border-black-300 bg-black-50 px-4 py-14 transition-colors disabled:pointer-events-none",
          !isParsingCsv &&
            "data-[dragging=true]:border-purple-400 data-[dragging=true]:bg-purple-50 hover:border-purple-400 hover:bg-purple-50",
        )}
        onClick={handleClick}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        disabled={isParsingCsv}
        aria-disabled={isParsingCsv}
      >
        {isParsingCsv ? (
          <Loader />
        ) : (
          <>
            <UploadIcon />
            <p className="text-center text-button-md">
              <span className="text-purple-500">Upload CSV document</span> or
              drag and drop
            </p>
            <p className="text-center text-button-sm text-black-500">
              Maximum file size 50MB, maximum rows 10.000
            </p>
          </>
        )}
      </button>

      <input
        ref={fileInputRef}
        id="csv-input"
        type="file"
        className="hidden"
        accept="text/csv"
        onChange={(e) => parseFile(e.currentTarget.files[0])}
      />

      <div className="flex items-center gap-x-0.5 text-xs text-black-700">
        <Info weight="fill" size={16} />
        <p>
          Additional info about List Campaigns and .CSV formatting:{" "}
          <a
            href="https://help.aimfox.com/en/articles/10827433-list-campaign-csv-upload-guide"
            target="_blank"
            rel="noreferrer"
            className="text-xs-bold underline transition-colors hover:text-purple-600"
          >
            Learn More
          </a>
        </p>
      </div>
    </section>
  );
}
